import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Ban, CheckCircle2, ChevronDown, XCircle } from 'lucide-react';
import { GroupContractMemberDto } from 'api/contract';
import { formatDay } from 'core/utils/dates';
import { StripedList } from 'back-office/shared/components/BlockCard';

const AMOUNT_FORMAT = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

/**
 * Les aliments bloqués d'abord.
 *
 * Le tri par identifiant place le problème n'importe où dans une liste qui peut compter
 * plusieurs dizaines de lignes. Or on n'ouvre cet écran que pour trouver ce qui ne passe
 * pas : à défaut de mandat en tête, puis les échéances abandonnées, puis les échecs.
 */
const byUrgency = (a: GroupContractMemberDto, b: GroupContractMemberDto): number =>
  Number(a.hasMandate) - Number(b.hasMandate) ||
  b.abandonedCount - a.abandonedCount ||
  b.failedCount - a.failedCount ||
  a.contractIdentifier.localeCompare(b.contractIdentifier, 'fr');

/**
 * Au-delà, la page devient illisible et lourde à rendre : vingt collectifs de plusieurs
 * centaines d'aliments feraient plusieurs milliers de lignes d'un coup. Le tri par
 * urgence ci-dessus garantit qu'un aliment replié n'est jamais un aliment en peine.
 */
const VISIBLE_MEMBERS = 25;

export const MemberRows: FC<{ members: GroupContractMemberDto[] }> = ({ members }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const sorted = useMemo(() => [...members].sort(byUrgency), [members]);
  const hidden = isExpanded ? 0 : Math.max(0, sorted.length - VISIBLE_MEMBERS);

  return (
    <>
      <MemberList members={hidden > 0 ? sorted.slice(0, VISIBLE_MEMBERS) : sorted} />
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="flex w-full items-center justify-center gap-1.5 border-t border-gray-100 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
        >
          <ChevronDown className="h-4 w-4" />
          Afficher les {hidden} aliments restants
        </button>
      )}
    </>
  );
};

const MemberList: FC<{ members: GroupContractMemberDto[] }> = ({ members }) => (
  <StripedList>
    {members.map((member) => (
      <li key={member.id} className="px-5 py-3">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold text-zinc-800">
                {member.contractIdentifier}
              </span>
              <MandateBadge member={member} />
              {member.statusLabel && (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                  {member.statusLabel}
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate text-sm text-gray-600">
              {member.insuredName || 'Assuré non renseigné'}
              {member.startDate && (
                <span className="text-gray-400"> · effet le {formatDay(member.startDate)}</span>
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <Counters member={member} />
            <span className="tabular-nums font-semibold text-zinc-800">
              {AMOUNT_FORMAT.format(member.totalAmount)}
            </span>
            <span className="w-28 text-right text-xs text-gray-500">
              {member.nextDueDate ? `au ${formatDay(member.nextDueDate)}` : 'aucune échéance'}
            </span>
            <Link
              to={`/back-office/contrats/details/${member.id}`}
              title="Ouvrir le contrat"
              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary/10 hover:text-primary"
            >
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </li>
    ))}
  </StripedList>
);

/**
 * Trois états, pas deux : un mandat rattaché mais non signé ne prélève pas davantage
 * qu'un mandat absent. Le back ne renseigne le RUM qu'à la signature — l'afficher en
 * vert sur la seule présence du rattachement laisserait croire le contrat en règle.
 */
const MandateBadge: FC<{ member: GroupContractMemberDto }> = ({ member }) => {
  if (!member.hasMandate) {
    return (
      <span
        title="Aucun mandat : aucun prélèvement ne peut partir pour ce contrat."
        className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800"
      >
        <XCircle className="h-3 w-3" />
        Sans mandat
      </span>
    );
  }

  if (!member.mandateRum) {
    return (
      <span
        title="Mandat rattaché mais non signé : le prélèvement reste impossible."
        className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-800"
      >
        <AlertTriangle className="h-3 w-3" />
        Mandat non signé
      </span>
    );
  }

  return (
    <span
      title={`RUM ${member.mandateRum}`}
      className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-800"
    >
      <CheckCircle2 className="h-3 w-3" />
      Mandat signé
    </span>
  );
};

/**
 * Compteurs d'échéances, les nuls masqués.
 *
 * Les compteurs du back s'emboîtent : `abandoned` ⊆ `failed` ⊆ `planned` (les trois
 * comptent des échéances au statut « planifiée », filtrées sur le nombre de tentatives).
 * Alignés côte à côte, « 3 en échec » et « 2 abandonnées » se liraient comme cinq
 * échéances en peine au lieu de trois dont deux définitivement sorties — d'où la
 * formulation en « dont », qui est la seule lecture juste.
 *
 * Afficher « 0 échec » sur chaque ligne noierait par ailleurs les rares lignes qui
 * portent un chiffre : seuls les compteurs qui appellent une action restent.
 */
const Counters: FC<{ member: GroupContractMemberDto }> = ({ member }) => (
  <span className="flex items-center gap-3 tabular-nums">
    <span className="text-gray-500" title="Échéances payées sur le total">
      <strong className="text-zinc-800">{member.paidCount}</strong>/{member.paymentCount} payées
    </span>
    {member.plannedCount > 0 && (
      <span className="text-gray-500" title="Échéances au statut « planifiée »">
        {member.plannedCount} planifiées
      </span>
    )}
    {member.failedCount > 0 && (
      <span
        className="inline-flex items-center gap-1 font-medium text-orange-700"
        title="Échéances planifiées ayant déjà échoué au moins une fois. Elles repassent dans le batch tant que les tentatives ne sont pas épuisées."
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        dont {member.failedCount} en échec
      </span>
    )}
    {member.abandonedCount > 0 && (
      <span
        className="inline-flex items-center gap-1 font-semibold text-red-700"
        title="Échéances sorties du batch après épuisement des tentatives. Elles ne repartiront pas d'elles-mêmes, même une fois le mandat corrigé."
      >
        <Ban className="h-3.5 w-3.5" />
        dont {member.abandonedCount} abandonnées
      </span>
    )}
  </span>
);
